import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import MovieCard from '@/components/base/MovieCard';
import { movies } from '@/mocks/movies';
import type { CategoryType, Movie } from '@/types/movie';
import { CATEGORY_LABELS, CATEGORY_INTRO } from '@/types/movie';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import FilterBar, { SORTS } from './components/FilterBar';
import Pagination from './components/Pagination';
import NotFound from '@/pages/NotFound';

const PAGE_SIZE = 12;
const VALID_TYPES: CategoryType[] = ['movie', 'tv', 'variety'];

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const category = (VALID_TYPES.includes(type as CategoryType) ? type : 'movie') as CategoryType;
  const isValid = VALID_TYPES.includes(type as CategoryType);

  const region = searchParams.get('region') || '全部';
  const year = searchParams.get('year') || '全部';
  const genre = searchParams.get('genre') || '全部';
  const sort = searchParams.get('sort') || 'recommend';
  const page = Math.max(1, Number(searchParams.get('page') || '1'));

  const label = CATEGORY_LABELS[category];

  useDocumentMeta({
    title: page > 1 ? `${label}频道 - 第 ${page} 页 - 妖精视频` : `${label}频道 - 妖精视频`,
    description: `${CATEGORY_INTRO[category]}，涵盖${region === '全部' ? '各国' : region}地区、${year === '全部' ? '各年份' : year + '年'}的优质${label}，支持按类型筛选与热度排序。`,
    keywords: `妖精视频,${label},${label}推荐,影视资源,在线影视`,
  });

  const filtered = useMemo(() => {
    let list: Movie[] = movies.filter((m) => m.category === category);

    if (region !== '全部') list = list.filter((m) => m.region === region);
    if (genre !== '全部') list = list.filter((m) => m.genres.includes(genre));
    if (year !== '全部') {
      if (year === '更早') list = list.filter((m) => m.year < 2022);
      else list = list.filter((m) => m.year === Number(year));
    }

    if (sort === 'heat') list.sort((a, b) => b.heat - a.heat);
    else if (sort === 'time') list.sort((a, b) => b.releaseDate.localeCompare(a.releaseDate));
    else list.sort((a, b) => b.heat * b.rating - a.heat * a.rating);

    return list;
  }, [category, region, year, genre, sort]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const currentPage = Math.min(page, Math.max(1, totalPages));
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === '全部' || (!value)) next.delete(key);
    else next.set(key, value);
    next.delete('page');
    setSearchParams(next);
  };

  const handleFilter = (key: 'region' | 'year' | 'genre', value: string) => updateParam(key, value);
  const handleSort = (value: string) => updateParam('sort', value);
  const handlePage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete('page');
    else next.set('page', String(p));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetFilters = () => setSearchParams(new URLSearchParams());

  if (!isValid) return <NotFound />;

  const hasFilter = region !== '全部' || year !== '全部' || genre !== '全部';

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 pt-6">
        <nav className="flex items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900 font-medium">{label}频道</span>
        </nav>

        <div className="mt-5 rounded-lg bg-primary-900 px-6 md:px-10 py-8">
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-background-50">
            {label}频道
          </h1>
          <p className="mt-2 text-sm text-primary-100">{CATEGORY_INTRO[category]}</p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-6">
        <FilterBar
          category={category}
          region={region}
          year={year}
          genre={genre}
          onChange={handleFilter}
        />

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-600">
            共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 部作品
          </p>
          <div className="inline-flex items-center gap-1 rounded-full bg-background-100 p-1">
            {SORTS.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => handleSort(s.key)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                  sort === s.key
                    ? 'bg-primary-500 font-semibold text-background-50'
                    : 'text-foreground-700 hover:text-primary-600'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {pageItems.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <Pagination page={currentPage} totalPages={totalPages} onPage={handlePage} />
          </>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-400">
              <i className="ri-emotion-sad-line"></i>
            </span>
            <h2 className="mt-5 font-heading text-lg font-bold text-foreground-900">没有找到匹配的作品</h2>
            <p className="mt-2 text-sm text-foreground-600">试试调整筛选条件，或重置后重新浏览</p>
            {hasFilter && (
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选条件
              </button>
            )}
          </div>
        )}
      </div>
    </SiteLayout>
  );
}